<script setup>
/**
 * The workroom's right-hand panel: the ticket's properties and its internal
 * thread, one at a time.
 *
 * The two used to be stacked regions competing for the same column, which cost
 * the thread the height it needs to be read. They are the same switch the inbox
 * makes between Details and Tola AI, so this is the same control — the shared
 * TabBar in the same header row as `Conversation/SidepanelTabs.vue`.
 *
 * The choice is remembered the way the inbox remembers its panel: through
 * `ui_settings` on the account user, so it survives a reload and follows the
 * agent to another machine rather than living in one browser's storage.
 *
 * Both bodies stay mounted and are switched with `v-show`: the thread scrolls
 * itself to the bottom on load, and a tab that unmounted it would restart that
 * every time an agent glanced at the properties.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const TABS = ['details', 'thread'];

// Anything other than the thread is the properties: an unset setting is a first
// visit, and a stale value is not worth a blank panel.
const activeTab = computed(() =>
  uiSettings.value?.support_case_sidepanel_tab === 'thread'
    ? 'thread'
    : 'details'
);

const tabs = computed(() => [
  { key: 'details', label: t('SUPPORT_CASES.DETAIL.SIDEPANEL.DETAILS') },
  { key: 'thread', label: t('SUPPORT_CASES.DETAIL.SIDEPANEL.THREAD') },
]);

const activeTabIndex = computed(() => TABS.indexOf(activeTab.value));

const onTabChanged = tab =>
  updateUISettings({ support_case_sidepanel_tab: tab.key });
</script>

<template>
  <section
    class="flex flex-col min-h-0 rounded-xl bg-n-alpha-1 h-[28rem] md:h-[34rem] xl:h-auto"
    data-testid="case-sidepanel"
    :aria-label="t('SUPPORT_CASES.DETAIL.SIDEPANEL.ARIA')"
  >
    <div
      class="flex items-center justify-between gap-2 px-3 py-2 border-b shrink-0 border-n-weak"
      data-testid="case-sidepanel-tabs"
    >
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="onTabChanged"
      />
    </div>

    <div
      v-show="activeTab === 'details'"
      class="min-h-0 px-3 py-3 overflow-y-auto grow"
      data-testid="case-sidepanel-details"
    >
      <slot name="details" />
    </div>

    <div
      v-show="activeTab === 'thread'"
      class="flex flex-col min-h-0 overflow-hidden grow"
      data-testid="case-sidepanel-thread"
    >
      <slot name="thread" />
    </div>
  </section>
</template>
